import React from "react";
import Box from "../Box";
import MrclText from "../marcel/MrclText";
import styled from "styled-components";
import useBreakPoint from "../../utils/useBreakPoint";
import BaseDashboardCard from "../BaseDashboardCard";
import { useTranslation } from "react-i18next";
import { formatCurrency, formatPercentage, getCurrencySuffix } from "../../utils/numberUtils";
import DeltaBadge from "../DeltaBadge";

type CommercialCardProps = {
  title: string;
  subTitle?: string;
  turnover: number;
  turnoverTotal?: string;
  importedDateTime?: string;
  rhRate: number;
  rhRateCurrency?: string;
  currency?: string;
  isUnavailable: boolean;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
  alignCenterMobileLandscape?: boolean;
};

/* Target BaseDashboardCard to ensure its inner content area fills height */
const StyledBaseDashboardCard = styled(BaseDashboardCard)<{ $alignCenterMobileLandscape?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 130px;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    height: auto !important;
    min-height: 120px !important;
    max-height: none !important;
    flex-shrink: 0 !important;
    overflow: hidden !important;
  }

  ${({ $alignCenterMobileLandscape }) =>
    $alignCenterMobileLandscape &&
    `
    /* Ensure the inner body container below the header flexes fully globally */
    & > div:last-child,
    .card-content,
    .c-card__content {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
      display: flex !important;
      flex-direction: column !important;

      /* Ensure the inner body container below the header flexes fully */
      & > div:last-child,
      .card-content,
      .c-card__content {
        flex: 1 !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        overflow: hidden !important;
      }
    }
  `}
`;

const CardsContainer = styled(Box)<{ $alignCenterMobileLandscape?: boolean }>`
  gap: ${(props) => `${props.theme.space?.[2]}px`};

  ${({ $alignCenterMobileLandscape }) =>
    $alignCenterMobileLandscape &&
    `
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
      flex: 1 !important;
      justify-content: center !important;
      margin-top: auto !important;
      margin-bottom: auto !important;
    }
  `}

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    padding: 8px !important;
    .c-card__content, .card-content {
      padding: 8px !important;
    }
  }
`;

const BoxContainer = styled(Box)`
  gap: ${(props) => `${props.theme.space?.[1]}px`};
`;

const TurnoverTotalWrapper = styled.div`
  min-height: 20px;
  margin-top: -10px;
  display: flex;
  align-items: center;
`;

const Text = styled(MrclText)`
  font-family: system-ui;
  margin: 0;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 14px !important;
    line-height: 1.2 !important;
  }
`;

const ValueText = styled(MrclText)`
  font-family: system-ui;
  margin: 0;

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    font-size: 24px !important;
    line-height: 1.1 !important;
  }
`;

const CommercialCard = ({
  title,
  subTitle,
  turnover,
  rhRate,
  rhRateCurrency,
  currency,
  turnoverTotal,
  importedDateTime,
  alignCenterMobileLandscape,
  ...res
}: CommercialCardProps) => {
  const isBreak = useBreakPoint();
  const { t } = useTranslation();

  /* Match title for Panier Moyen or Débits */
  const isPanierMoyenOrDebits =
    title === t("commercial.panier_moyen") ||
    title === t("commercial.debits") ||
    title === "commercial.panier_moyen" ||
    title === "commercial.debits";

  const shouldCenterLandscape = alignCenterMobileLandscape ?? isPanierMoyenOrDebits;

  const getFormattedValue = () => {
    if (!turnover || isNaN(turnover)) return null;

    switch (currency) {
      case "€":
        return formatCurrency(turnover, "");
      case "k":
        return turnover >= 1000 ? formatCurrency(turnover, "") : turnover;
      case "%":
        return formatPercentage(turnover, "");
      default:
        return turnover;
    }
  };

  const getSuffix = () => {
    if (currency === "€") {
      return getCurrencySuffix(turnover / 1000);
    }
    if (currency === "%") {
      return "%";
    }
    if (currency === "k" && turnover >= 1000) {
      return "k";
    }
    return "";
  };

  return (
    <StyledBaseDashboardCard
      width="100%"
      justifyContent={"center"}
      data-testid="commercial-card"
      {...res}
      hasModal={false}
      title={title}
      subTitle={subTitle}
      size={"small"}
      importedDateTime={importedDateTime}
      $alignCenterMobileLandscape={shouldCenterLandscape}
    >
      <CardsContainer
        display="flex"
        flexDirection="column"
        $alignCenterMobileLandscape={shouldCenterLandscape}
      >
        {getFormattedValue() !== null ? (
          <ValueText tagStyle={isBreak === "phone" ? "h1" : "display"}>
            {getFormattedValue()}
            <Text size={"m"}>
              {getSuffix()}
            </Text>
          </ValueText>
        ) : (
          <ValueText tagStyle={"h1"}>-</ValueText>
        )}
        <TurnoverTotalWrapper>
          {turnoverTotal && (
            <Text
              size="s"
              style={{ color: "#575757" }}
            >
              {turnoverTotal}
            </Text>
          )}
        </TurnoverTotalWrapper>
        <BoxContainer
          display="flex"
          alignItems="center"
          justifyContent="center"
          data-testid="Ctag"
        >
          {rhRate == null || isNaN(rhRate) ? (
            "-"
          ) : (
            <DeltaBadge
              value={rhRate}
              suffix={rhRateCurrency ?? "%"}
            />
          )}
          <Text
            size={isBreak === "phone" ? "s" : "l"}
            style={{ color: "#575757" }}
          >
            {"vs "} {t("commercial.n_1")}
          </Text>
        </BoxContainer>
      </CardsContainer>
    </StyledBaseDashboardCard>
  );
};

export default CommercialCard;